import { useState, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Phone,
  Users,
  MessageSquare,
  Mail,
  Coffee,
  UtensilsCrossed,
  Video,
  MoreHorizontal,
  Loader2,
  Search,
  X,
  UserPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PlaceAutocomplete } from '@/components/PlaceAutocomplete'
import { useContact, useContacts } from '@/hooks/use-contacts'
import { useCreateInteraction, useUpdateInteraction } from '@/hooks/use-interactions'
import type { Contact, InteractionType, InteractionQuality } from '@/lib/types'

const INTERACTION_TYPES: { type: InteractionType; icon: typeof Phone; label: string }[] = [
  { type: 'call', icon: Phone, label: 'Call' },
  { type: 'meeting', icon: Users, label: 'Meeting' },
  { type: 'text', icon: MessageSquare, label: 'Text' },
  { type: 'email', icon: Mail, label: 'Email' },
  { type: 'coffee', icon: Coffee, label: 'Coffee' },
  { type: 'dinner', icon: UtensilsCrossed, label: 'Dinner' },
  { type: 'video_call', icon: Video, label: 'Video' },
  { type: 'other', icon: MoreHorizontal, label: 'Other' },
]

const QUALITY_OPTIONS: {
  value: InteractionQuality
  emoji: string
  label: string
  color: string
}[] = [
  {
    value: 'great',
    emoji: '\u{1F929}',
    label: 'Great',
    color: 'bg-chartreuse-soft text-chartreuse-foreground ring-chartreuse',
  },
  {
    value: 'good',
    emoji: '\u{1F60A}',
    label: 'Good',
    color: 'bg-lilac-soft text-lilac-foreground ring-lilac',
  },
  {
    value: 'neutral',
    emoji: '\u{1F610}',
    label: 'Neutral',
    color: 'bg-muted text-muted-foreground ring-outline',
  },
  {
    value: 'poor',
    emoji: '\u{1F61E}',
    label: 'Poor',
    color: 'bg-coral-soft text-coral ring-coral',
  },
]

function formatDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function getInitials(firstName: string, lastName: string | null): string {
  return firstName.charAt(0).toUpperCase() + (lastName?.charAt(0).toUpperCase() ?? '')
}

function getContactName(c: { firstName: string; lastName: string | null }): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ')
}

export default function LogInteractionPage() {
  const { id: paramContactId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Edit mode: interaction data passed via location state
  const editData = location.state?.interaction as
    | {
        id: string
        type: string
        title: string | null
        notes: string | null
        location: string | null
        meetingContext: string | null
        quality: string | null
        occurredAt: string
        contactId: string
        participants?: {
          id: string
          firstName: string
          lastName: string | null
          avatarUrl: string | null
        }[]
      }
    | undefined
  const isEdit = !!editData

  // Contact ID from param, search param, or edit data
  const resolvedContactId =
    paramContactId || searchParams.get('contact_id') || editData?.contactId || ''

  const { data: prefillContact } = useContact(resolvedContactId || undefined)
  const createInteraction = useCreateInteraction()
  const updateInteraction = useUpdateInteraction()

  // Multi-contact state: track selected contacts as array
  const [selectedContacts, setSelectedContacts] = useState<
    { id: string; firstName: string; lastName: string | null; avatarUrl: string | null }[]
  >(() => {
    // Initialize from edit data participants or from prefill contact
    if (editData?.participants?.length) {
      return editData.participants
    }
    return []
  })

  // Contact picker state
  const [contactSearch, setContactSearch] = useState('')
  const [showContactPicker, setShowContactPicker] = useState(!resolvedContactId && !isEdit)
  const { data: contactResults } = useContacts({ q: contactSearch, limit: 10 })

  // When prefillContact loads and selectedContacts is empty, initialize it
  const hasInitialized = useMemo(() => {
    if (prefillContact && selectedContacts.length === 0 && !showContactPicker) {
      return false
    }
    return true
  }, [prefillContact, selectedContacts.length, showContactPicker])

  if (!hasInitialized && prefillContact) {
    setSelectedContacts([
      {
        id: prefillContact.id,
        firstName: prefillContact.firstName,
        lastName: prefillContact.lastName,
        avatarUrl: prefillContact.avatarUrl,
      },
    ])
  }

  // Form state
  const [type, setType] = useState<InteractionType>((editData?.type as InteractionType) || 'call')
  const [occurredAt, setOccurredAt] = useState(
    formatDateTimeLocal(editData ? new Date(editData.occurredAt) : new Date()),
  )
  const [title, setTitle] = useState(editData?.title || '')
  const [notes, setNotes] = useState(editData?.notes || '')
  const [formLocation, setFormLocation] = useState(editData?.location || '')
  const [meetingContext, setMeetingContext] = useState(editData?.meetingContext || '')
  const [showMeetingContext, setShowMeetingContext] = useState(!!editData?.meetingContext)
  const [quality, setQuality] = useState<InteractionQuality | null>(
    (editData?.quality as InteractionQuality) || null,
  )

  const isPending = createInteraction.isPending || updateInteraction.isPending

  function addContact(c: Contact) {
    if (selectedContacts.some((sc) => sc.id === c.id)) return
    setSelectedContacts((prev) => [
      ...prev,
      { id: c.id, firstName: c.firstName, lastName: c.lastName, avatarUrl: c.avatarUrl },
    ])
    setContactSearch('')
    setShowContactPicker(false)
  }

  function removeContact(contactId: string) {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId))
  }

  function handleSave() {
    if (selectedContacts.length === 0) return

    const contactIds = selectedContacts.map((c) => c.id)

    const payload = {
      contact_ids: contactIds,
      type,
      title: title.trim() || null,
      notes: notes.trim() || null,
      location: formLocation.trim() || null,
      meeting_context: meetingContext.trim() || null,
      quality,
      occurred_at: new Date(occurredAt).toISOString(),
    }

    if (isEdit && editData) {
      updateInteraction.mutate(
        { id: editData.id, ...payload },
        {
          onSuccess: () => {
            const backTo = paramContactId || selectedContacts[0]?.id
            navigate(`/contacts/${backTo}`, { state: { tab: 'timeline' } })
          },
        },
      )
    } else {
      createInteraction.mutate(payload, {
        onSuccess: () => {
          const backTo = paramContactId || selectedContacts[0]?.id
          navigate(`/contacts/${backTo}`)
        },
      })
    }
  }

  function handleBack() {
    if (paramContactId) {
      navigate(`/contacts/${paramContactId}`)
    } else if (selectedContacts.length > 0) {
      navigate(`/contacts/${selectedContacts[0].id}`)
    } else {
      navigate(-1)
    }
  }

  // Filter out already-selected contacts from search results
  const filteredResults = contactResults?.items.filter(
    (c) => !selectedContacts.some((sc) => sc.id === c.id),
  )

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="border-outline-variant/20 flex items-center gap-2 border-b px-6 py-4">
        <button
          type="button"
          onClick={handleBack}
          className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading text-lg font-semibold">
            {isEdit
              ? t('interactions.edit', 'Edit Interaction')
              : t('interactions.log', 'Log Interaction')}
          </h1>
          {selectedContacts.length > 0 && !showContactPicker && (
            <p className="text-on-surface-variant text-sm">
              {selectedContacts.length === 1
                ? getContactName(selectedContacts[0])
                : t('interactions.participants', '{{count}} participants', {
                    count: selectedContacts.length,
                  })}
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg space-y-6 px-6 pb-8">
          {/* Contact(s) selection */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('interactions.contacts', 'Contacts')}
            </label>

            {/* Selected contacts chips */}
            {selectedContacts.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {selectedContacts.map((c) => (
                  <div
                    key={c.id}
                    className="bg-surface-container-low flex items-center gap-1.5 rounded-full py-1 pr-2 pl-1"
                  >
                    {c.avatarUrl ? (
                      <img src={c.avatarUrl} alt="" className="size-6 rounded-full object-cover" />
                    ) : (
                      <div className="bg-primary/10 text-primary flex size-6 items-center justify-center rounded-full text-[10px] font-semibold">
                        {getInitials(c.firstName, c.lastName)}
                      </div>
                    )}
                    <span className="text-xs font-medium">{getContactName(c)}</span>
                    <button
                      type="button"
                      onClick={() => removeContact(c.id)}
                      className="text-muted-foreground hover:text-foreground ml-0.5"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add participant button / search */}
            {showContactPicker ? (
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder={t('interactions.searchContact', 'Search contacts...')}
                  className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border py-2 pr-3 pl-9 text-sm outline-none focus:ring-2"
                  autoFocus
                />
                {contactSearch && filteredResults && filteredResults.length > 0 && (
                  <div className="border-outline-variant/20 bg-surface-container-highest/90 absolute top-full right-0 left-0 z-10 mt-1 max-h-48 overflow-y-auto rounded-xl shadow-lg backdrop-blur-xl">
                    {filteredResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="hover:bg-primary/5 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                        onClick={() => addContact(c)}
                      >
                        {c.avatarUrl ? (
                          <img
                            src={c.avatarUrl}
                            alt=""
                            className="size-7 rounded-full object-cover"
                          />
                        ) : (
                          <div className="bg-primary/10 text-primary flex size-7 items-center justify-center rounded-full text-xs font-semibold">
                            {getInitials(c.firstName, c.lastName)}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">{getContactName(c)}</span>
                          {c.company && (
                            <span className="text-muted-foreground ml-1 text-xs">{c.company}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {selectedContacts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowContactPicker(false)
                      setContactSearch('')
                    }}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowContactPicker(true)}
                className="border-outline-variant/30 hover:bg-surface-container-high text-on-surface-variant flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm transition-colors"
              >
                <UserPlus className="size-4" />
                {selectedContacts.length === 0
                  ? t('interactions.selectContact', 'Select a contact')
                  : t('interactions.addParticipant', 'Add participant')}
              </button>
            )}
          </div>

          {/* Type selector */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('interactions.type', 'Type')}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {INTERACTION_TYPES.map(({ type: t, icon: Icon, label }) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-colors',
                    type === t
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Date/time */}
          <div>
            <label htmlFor="occurred-at" className="mb-2 block text-sm font-medium">
              {t('interactions.when', 'When')}
            </label>
            <input
              id="occurred-at"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="mb-2 block text-sm font-medium">
              {t('interactions.title', 'Title')}
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('interactions.titlePlaceholder', 'Optional title...')}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="mb-2 block text-sm font-medium">
              {t('interactions.notes', 'Notes')}
            </label>
            <textarea
              id="notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('interactions.notesPlaceholder', 'What did you talk about?')}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="mb-2 block text-sm font-medium">
              {t('interactions.location', 'Location')}
            </label>
            <PlaceAutocomplete
              id="location"
              value={formLocation}
              onChange={setFormLocation}
              placeholder={t('interactions.locationPlaceholder', 'Optional location...')}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Meeting context (expandable) */}
          <div>
            <button
              type="button"
              onClick={() => setShowMeetingContext(!showMeetingContext)}
              className="text-on-surface-variant hover:text-foreground flex w-full items-center gap-2 text-sm font-medium transition-colors"
            >
              {showMeetingContext ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
              {t('interactions.meetingContext', 'Meeting context')}
            </button>
            {showMeetingContext && (
              <textarea
                id="meeting-context"
                rows={3}
                value={meetingContext}
                onChange={(e) => setMeetingContext(e.target.value)}
                placeholder={t(
                  'interactions.meetingContextPlaceholder',
                  'When, where, how, what...',
                )}
                className="border-outline-variant/30 bg-surface-container focus:ring-ring mt-2 w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              />
            )}
          </div>

          {/* Quality */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('interactions.quality', 'Quality')}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {QUALITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setQuality(quality === opt.value ? null : opt.value)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-colors',
                    quality === opt.value
                      ? `${opt.color} ring-2`
                      : 'border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high',
                  )}
                >
                  <span className="text-lg">{opt.emoji}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="border-outline-variant/20 border-t p-6">
        <button
          type="button"
          onClick={handleSave}
          disabled={isPending || selectedContacts.length === 0}
          className="bg-primary text-primary-foreground flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? t('common.save', 'Save') : t('common.save', 'Save')}
        </button>
      </div>
    </div>
  )
}
